import {type ComponentType, type ReactNode, useCallback} from 'react';

import classnames from 'classnames';
import {motion} from 'framer-motion';

import {getUserPrefersReducedMotion} from 'nutshell-core/utils/get-user-prefers-reduced-motion';

import {PageControlBar} from '../../control-bar/page-control-bar';
import type {TabProps} from '../../control-bar/page-control-bar';
import {CardWidgetEmptyState} from '../../empty-states/card-widget-empty-state';
import type {SelectMenuOption} from '../../select-menu/select-menu';
import {getLoadingComponent, getWidgetIconForType} from '../../widgets/helpers';
import type {RealAndFakeWidgetType} from '../../widgets/types';

import {CardWidgetHeader} from './card-widget-header';
import {WidgetErrorBanner} from './widget-error-banner';

import styles from './card-widget.module.css';

export type BaseProps = {
    children: ReactNode | null | undefined;
    // For passing feature gate upgrade links or other secondary content to be
    // shown to the right of the title
    secondaryTitle?: ReactNode;
    type?: RealAndFakeWidgetType;
    upperCaseTitle?: boolean;
    isFullWidth?: boolean;
    isLoading?: boolean;
    isFullHeight?: boolean;
    minExpandedHeight?: number;
    rowsHavePadding?: boolean;
    titleHasDivider?: boolean;
    hasBorder?: boolean;
    count?: number;
    showHeaderSpinner?: boolean;
};

export type ErrorProps = {
    error: string | null | undefined;
    setError: (error?: string | null | undefined) => void;
};

export type EmptyStateProps = {
    isEmpty: boolean;
    text: string;
    icon?: ComponentType<any>;
    iconSize?: number;
    illustration?: ReactNode;
    cta?: React.ReactNode;
};

export type AddButtonProps = AddButtonPropsSingular | AddButtonPropsMultiple;

export type AddButtonPropsSingular = {
    variant: 'singular';
    onClick: () => void;
    label?: string;
    isDisabled?: boolean;
    getButtonRef?: (ref: HTMLButtonElement | null | undefined) => void;
};

export type AddButtonPropsMultiple = {
    variant: 'multiple';
    onClick?: () => void;
    label?: string;
    selectMenuOptions: SelectMenuOption[];
    isDisabled?: boolean;
    getButtonRef?: (ref: HTMLButtonElement | null | undefined) => void;
};

type CollapsibleProps = {
    isCollapsed: boolean;
    onToggle: () => void;
    collapsedContent?: ReactNode;
};

export type WidgetTabProps = {
    tabs?: TabProps;
    headerContent?: ReactNode;
};

type Props = {
    title: string;
} & BaseProps & {
        tabProps?: WidgetTabProps;
        // If initial collapse state is based on a User setting, use
        // core/card/card-widget-wrapper.js
        collapsibleProps?: CollapsibleProps;
        addButtonProps?: AddButtonProps;
        secondaryAddButtonProps?: AddButtonProps;
        errorProps?: ErrorProps;
        emptyStateProps?: EmptyStateProps;
        cardRef?: (arg1?: HTMLDivElement | null | undefined) => void;
    };

// Animation variants for collapse/expand
const ANIMATION_VARIANTS = {
    collapsed: {
        height: 0,
        opacity: 0,
        overflow: 'hidden',
    },
    expanded: {
        height: 'auto',
        opacity: 1,
        overflow: 'visible',
    },
} as const;

export function CardWidget(props: Props) {
    const {upperCaseTitle = true, rowsHavePadding = true, hasBorder = true} = props;

    const isCollapsed = props.collapsibleProps && props.collapsibleProps.isCollapsed;
    const isEmpty = props.emptyStateProps ? props.emptyStateProps.isEmpty : false;

    const shouldShowEmptyOrLoadingSection =
        // Show loading state for collapsed widgets if the widget has collapsed
        // content
        (isCollapsed &&
            props.collapsibleProps &&
            props.collapsibleProps.collapsedContent &&
            props.isLoading) ||
        (!isCollapsed && (isEmpty || props.isLoading));

    const title = upperCaseTitle ? props.title.toUpperCase() : props.title;

    const clearErrorOnHidden = useCallback(() => {
        if (props.errorProps && props.errorProps.setError) {
            props.errorProps.setError(null);
        }
    }, [props.errorProps]);

    const onToggleCollapse = useCallback(() => {
        if (!isCollapsed) {
            clearErrorOnHidden();
        }
        if (props.collapsibleProps) {
            props.collapsibleProps.onToggle();
        }
    }, [isCollapsed, clearErrorOnHidden, props.collapsibleProps]);

    const collapsibleContentStyle = classnames({
        [styles['content-shown']]: isCollapsed,
        [styles['jQuery-compatible-hidden']]: !isCollapsed,
        [styles['has-padding']]: !rowsHavePadding,
    });

    const fullContentStyle = classnames({
        [styles['content-shown']]: !isCollapsed,
        [styles['jQuery-compatible-hidden']]: isCollapsed || isEmpty,
        [styles['has-padding']]: !rowsHavePadding,
    });

    const cardStyleNames = classnames(styles['card-widget'], {
        [styles['card-widget--bordered']]: hasBorder,
        [styles['card-widget--full-width']]: props.isFullWidth,
        [styles['card-widget--full-height']]: props.isFullHeight,
        [styles['card-widget--ai-variant']]:
            props.type === 'AI_SUMMARY' || props.type === 'LEAD_RESEARCHER',
    });

    const TitleIcon = getWidgetIconForType(props.type);
    const loadingComponent = getLoadingComponent(props.type);

    return (
        <div ref={props.cardRef} className={cardStyleNames}>
            <CardWidgetHeader
                title={title}
                titleIcon={TitleIcon}
                secondaryTitle={props.secondaryTitle}
                isCollapsible={Boolean(props.collapsibleProps)}
                isCollapsed={Boolean(props.collapsibleProps && props.collapsibleProps.isCollapsed)}
                onToggleCollapse={onToggleCollapse}
                addButtonProps={props.addButtonProps}
                secondaryAddButtonProps={props.secondaryAddButtonProps}
                hasDivider={props.titleHasDivider}
                count={props.count}
                type={props.type}
                showSpinner={props.showHeaderSpinner}
            />
            {props.errorProps && props.errorProps.error && (
                <div className='mt-8 mx-12'>
                    <WidgetErrorBanner
                        error={props.errorProps.error}
                        setError={props.errorProps.setError}
                    />
                </div>
            )}
            <motion.div
                initial={false}
                animate={!isCollapsed ? 'expanded' : 'collapsed'}
                variants={ANIMATION_VARIANTS}
                transition={{
                    duration: getUserPrefersReducedMotion() ? 0 : 0.15,
                    ease: 'easeInOut',
                }}
            >
                {props.tabProps &&
                    props.tabProps.headerContent &&
                    !isCollapsed &&
                    props.tabProps.headerContent}
                {props.tabProps && props.tabProps.tabs && !isCollapsed && (
                    <div className={styles['tab-container']}>
                        <PageControlBar tabs={props.tabProps.tabs} lessPadding={true} />
                    </div>
                )}
                {props.children && !props.isLoading && (
                    <div
                        className={fullContentStyle}
                        style={{
                            minHeight: !isCollapsed ? props.minExpandedHeight : undefined,
                        }}
                    >
                        {props.children}
                    </div>
                )}
            </motion.div>
            {shouldShowEmptyOrLoadingSection && (
                <div
                    className={styles['loading-and-empty-state']}
                    style={{
                        minHeight: !isCollapsed ? props.minExpandedHeight : undefined,
                    }}
                >
                    {props.isLoading && loadingComponent}
                    {isEmpty && !props.isLoading && props.emptyStateProps && (
                        <CardWidgetEmptyState
                            text={props.emptyStateProps.text}
                            icon={props.emptyStateProps.icon}
                            iconSize={props.emptyStateProps.iconSize}
                            illustration={props.emptyStateProps.illustration}
                            cta={props.emptyStateProps.cta}
                        />
                    )}
                </div>
            )}
            {!props.isLoading &&
                props.collapsibleProps &&
                props.collapsibleProps.collapsedContent && (
                    <div className={collapsibleContentStyle}>
                        {props.collapsibleProps.collapsedContent}
                    </div>
                )}
        </div>
    );
}
